import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { ChevronRight, Eye, FileText, Plus } from 'lucide-react';
import {
  AqilaButton,
  AqilaPageHeader,
  AqilaSearchInput,
  AqilaTable,
  AqilaTableBody,
  AqilaTableCell,
  AqilaTableHead,
  AqilaTableHeader,
  AqilaTableRow,
} from '@agro1desenvolvimento/agro1-design-system/aqila';
import RelatorioVisitaService, {
  type RelatorioVisitaListItem,
} from '../../../services/relatorio-visita';
import useAsyncFetch from '../../../hooks/use-async';
import useLocalStore from '../../../hooks/use-local-store';

const DATE_FORMAT = 'DD/MM/YYYY';
const DATETIME_FORMAT = 'DD/MM/YYYY HH:mm';

const RelatorioVisitaListPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { value: currentPropriedade } = useLocalStore('currentPropriedade');
  const propriedadeId = currentPropriedade?.id;

  const { data: relatorios, loading } = useAsyncFetch(
    {
      callback: () =>
        RelatorioVisitaService.list(
          propriedadeId ? { propriedade: propriedadeId } : {},
        ),
      errorMessage: 'Falha ao carregar os relatórios de visita.',
      withLoader: true,
    },
    [propriedadeId],
  );

  const filtered = useMemo(() => {
    const list = [...(relatorios ?? [])].sort(
      (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
    );
    const term = search.trim().toLowerCase();

    if (!term) return list;

    return list.filter((relatorio) => {
      const usuario = relatorio.usuario?.nomeCompleto ?? '';
      const data = dayjs(relatorio.data).format(DATE_FORMAT);

      return (
        String(relatorio.id).includes(term) ||
        usuario.toLowerCase().includes(term) ||
        data.includes(term)
      );
    });
  }, [relatorios, search]);

  const total = relatorios?.length ?? 0;

  return (
    <div className="rv-list-page">
      <div className="rv-list-breadcrumb">
        <FileText className="rv-list-crumb-active" size={16} />
        <span className="rv-list-crumb-active">Relatórios</span>
        <ChevronRight className="rv-list-crumb-sep" size={16} />
        <span className="rv-list-crumb-current">Visitas Técnicas</span>
      </div>

      <div className="rv-list-content">
        <AqilaPageHeader
          className="rv-list-header"
          action={
            <AqilaButton
              size="sm"
              className="rv-list-new-btn"
              type="button"
              onClick={() => navigate('/relatorio-visita/novo')}
            >
              <Plus />
              Novo
            </AqilaButton>
          }
          subtitle={`${total} ${
            total === 1 ? 'relatório gerado' : 'relatórios gerados'
          }`}
          title="Relatório de visitas"
        />

        <div className="rv-list-search">
          <AqilaSearchInput
            placeholder="Buscar relatório..."
            value={search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(event.target.value)
            }
          />
        </div>

        <AqilaTable bordered>
          <AqilaTableHeader>
            <AqilaTableRow>
              <AqilaTableHead>#</AqilaTableHead>
              <AqilaTableHead>Data do relatório</AqilaTableHead>
              <AqilaTableHead>Usuário</AqilaTableHead>
              <AqilaTableHead>Criado em</AqilaTableHead>
              <AqilaTableHead className="text-right">Ações</AqilaTableHead>
            </AqilaTableRow>
          </AqilaTableHeader>
          <AqilaTableBody>
            {filtered.length === 0 ? (
              <AqilaTableRow>
                <AqilaTableCell
                  className="text-center text-aqila-muted"
                  colSpan={5}
                >
                  {loading
                    ? 'Carregando relatórios...'
                    : 'Nenhum relatório encontrado.'}
                </AqilaTableCell>
              </AqilaTableRow>
            ) : (
              filtered.map((relatorio: RelatorioVisitaListItem) => (
                <AqilaTableRow key={relatorio.uuid}>
                  <AqilaTableCell className="text-aqila-muted">
                    {relatorio.id}
                  </AqilaTableCell>
                  <AqilaTableCell>
                    {dayjs(relatorio.data).format(DATE_FORMAT)}
                  </AqilaTableCell>
                  <AqilaTableCell>
                    {relatorio.usuario?.nomeCompleto ?? '-'}
                  </AqilaTableCell>
                  <AqilaTableCell className="text-aqila-muted">
                    {dayjs(relatorio.createdAt).format(DATETIME_FORMAT)}
                  </AqilaTableCell>
                  <AqilaTableCell className="text-right">
                    <AqilaButton
                      aria-label="Visualizar relatório"
                      size="icon-sm"
                      title="Visualizar relatório"
                      type="button"
                      onClick={() =>
                        navigate(`/relatorio-visita-aqila/${relatorio.uuid}`)
                      }
                    >
                      <Eye />
                    </AqilaButton>
                  </AqilaTableCell>
                </AqilaTableRow>
              ))
            )}
          </AqilaTableBody>
        </AqilaTable>
      </div>
    </div>
  );
};

export default RelatorioVisitaListPage;
